import { useMemo, useState } from 'react';
import {
    Box, Button, Card, CardActionArea, Collapse, CssBaseline, FormControl, InputLabel,
    MenuItem, Select, Stack, TextField, ThemeProvider, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { createAppTheme } from '../../theme/theme';
import ApplicationAppService from '../../application/platform/application-app-service';
import { getProductName } from '../../application/platform/govhub-brand';
import LocalEnvironment, {
    defaultLocalAuthenticationUrl, localModules, localTenants,
} from '../../application/platform/local-environment';

// Tela exibida apenas em localhost, antes da aplicação, para escolher módulo e multitenant.
export function LocalEnvironmentSelector() {
    const current = LocalEnvironment.get();

    const [moduleId, setModuleId] = useState<number>(current?.moduleId ?? localModules[0].moduleId);
    const [multitenant, setMultitenant] = useState<number>(current?.multitenant ?? localTenants[1].multitenant);
    const [baseUrl, setBaseUrl] = useState<string>(current?.baseUrl ?? LocalEnvironment.getModule(moduleId)!.baseUrl);
    const [baseUrlAuthentication, setBaseUrlAuthentication] = useState<string>(current?.baseUrlAuthentication ?? defaultLocalAuthenticationUrl);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const theme = useMemo(() => createAppTheme(moduleId), [moduleId]);

    const selectModule = (id: number) => {
        setModuleId(id);
        setBaseUrl(LocalEnvironment.getModule(id)!.baseUrl);
    };

    const start = () => {
        LocalEnvironment.set({
            moduleId,
            multitenant,
            baseUrl: baseUrl.trim(),
            baseUrlAuthentication: baseUrlAuthentication.trim(),
        });
        // A sessão anterior pertence a outro módulo/multitenant; o recarregamento refaz as
        // configurações lidas na carga dos módulos (axios, rotas, tema).
        ApplicationAppService.clearAuthenticationData();
        window.location.assign('/');
    };

    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100', p: 2 }}>
                <Box sx={{ width: '100%', maxWidth: 720, bgcolor: 'background.paper', borderRadius: 2, boxShadow: 1, p: { xs: 2, sm: 4 } }}>
                    <Typography variant="overline" color="text.secondary">Ambiente local</Typography>
                    <Typography variant="h5" fontWeight={600} gutterBottom>Escolha o módulo e o multitenant</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        A escolha fica salva neste navegador. Para trocar depois, use o botão de ambiente no topo da tela.
                    </Typography>

                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Módulo</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(200px, 1fr))' }, gap: 1.5, mb: 3 }}>
                        {localModules.map(module => {
                            const selected = module.moduleId === moduleId;
                            const color = createAppTheme(module.moduleId).palette.primary.main;
                            return (
                                <Card
                                    key={module.moduleId}
                                    variant="outlined"
                                    sx={{ borderColor: selected ? color : 'divider', borderWidth: selected ? 2 : 1, bgcolor: selected ? `${color}0F` : undefined }}
                                >
                                    <CardActionArea onClick={() => selectModule(module.moduleId)} sx={{ p: 1.5, height: '100%' }}>
                                        <Stack direction="row" spacing={1.5} alignItems="center">
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography fontWeight={600}>{getProductName(module.moduleId)}</Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">{module.moduleDescription}</Typography>
                                            </Box>
                                        </Stack>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Box>

                    <FormControl fullWidth sx={{ mb: 2 }}>
                        <InputLabel id="local-tenant-label">Multitenant</InputLabel>
                        <Select
                            labelId="local-tenant-label"
                            label="Multitenant"
                            value={multitenant}
                            onChange={e => setMultitenant(Number(e.target.value))}
                        >
                            {localTenants.map(tenant => (
                                <MenuItem key={tenant.multitenant} value={tenant.multitenant}>
                                    {tenant.name} ({tenant.multitenant})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Button
                        size="small"
                        color="inherit"
                        onClick={() => setShowAdvanced(v => !v)}
                        endIcon={<ExpandMoreIcon sx={{ transform: showAdvanced ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
                        sx={{ mb: 1, color: 'text.secondary' }}
                    >
                        Avançado
                    </Button>
                    <Collapse in={showAdvanced}>
                        <Stack spacing={2} sx={{ mb: 2 }}>
                            <TextField label="URL da API do módulo" value={baseUrl} onChange={e => setBaseUrl(e.target.value)} fullWidth size="small" />
                            <TextField label="URL da API de autenticação" value={baseUrlAuthentication} onChange={e => setBaseUrlAuthentication(e.target.value)} fullWidth size="small" />
                        </Stack>
                    </Collapse>

                    <Button variant="contained" size="large" fullWidth onClick={start} disabled={!baseUrl.trim() || !baseUrlAuthentication.trim()}>
                        Entrar no {getProductName(moduleId)}
                    </Button>
                </Box>
            </Box>
        </ThemeProvider>
    );
}
